import { useState, useEffect, useCallback, useRef } from 'react'
import { recordBrowserHttpErrorResponse } from '@secureblob/sdk/browserHttpErrors'
import { API_BASE_URL, APP_BASE_PATH } from '../../config'
import './AdminPage.css'

/** Admin cachestats endpoint — under /api/cryptoblobstore/admin */
const CACHESTATS_URL = `${API_BASE_URL}/admin/cachestats`
const DISK_CACHE_TRUNCATE_URL = `${API_BASE_URL}/admin/disk-cache/truncate`
/** Cost/rate-limiter endpoint */
const COST_URL = `${API_BASE_URL}/cost`

function recordUnexpectedAdminResponse(response: Pick<Response, 'status' | 'statusText'>, url: string, method = 'GET'): void {
    if (response.status === 401) {
        return
    }

    recordBrowserHttpErrorResponse(response, { url, method })
}

// ── Types ────────────────────────────────────────────────────
// NOTE: Property names MUST be camelCase to match ASP.NET Core's default
// System.Text.Json serialization (JsonNamingPolicy.CamelCase).

interface CacheLayer {
    hitRate: number
    hits: number
    misses: number
    evictions: number
    entryCount: number
    estimatedBytes: number
}

interface DiskCacheLayer extends CacheLayer {
    writes: number
}

interface AttributionKey {
    workspaceType: string
    workload: string
    origin: string
    job: string
}

interface DiskCacheAttribution {
    key: AttributionKey
    blobHits: number
    blobMisses: number
    blobWrites: number
    listHits: number
    listMisses: number
    listWrites: number
    avoidedAzureReads: number
    avoidedAzureLists: number
    avoidedAzureEgressBytes: number
}

interface DiskCacheData {
    enabled: boolean
    rootPath: string
    maxBytes: number
    highWatermarkBytes: number
    lowWatermarkBytes: number
    minFreeBytes: number
    singleStorageWriterMode: boolean
    cacheBlobDownloads: boolean
    cacheMutableBlobDownloads: boolean
    cacheListOperations: boolean
    listCacheTtlMinutes: number
    estimatedBytes: number
    entryCount: number
    blobEntries: number
    listEntries: number
    artifactEntries: number
    blobCache: DiskCacheLayer
    listCache: DiskCacheLayer
    listInvalidations: number
    uncacheableTruncatedLists: number
    corruptEntries: number
    truncateRuns: number
    trimRuns: number
    startupScanEnabled: boolean
    startupScanCompleted: boolean
    lastTrimAt: string | null
    lastStartupScanAt: string | null
    lastError: string | null
    avoidedAzureReads: number
    avoidedAzureLists: number
    avoidedAzureEgressBytes: number
    attributions?: DiskCacheAttribution[]
}

interface PressureData {
    zone: string
    pressure: number
    processPressure: number
    fragmentation: number
    gen2Rate: number
    workingSetBytes: number
    availableMemoryBytes: number
    highMemoryLoadThresholdBytes: number
    compactsYellow: number
    compactsRed: number
    compactsCritical: number
    largeObjectEvictions: number
    largeObjectsTracked: number
    lastSample: string
}

interface BudgetData {
    objectSizeLimit: number
    headsSizeLimit: number
    configSizeLimit: number
}

interface StatsResponse {
    objectCache: CacheLayer
    headsCache: CacheLayer
    configCache: CacheLayer
    pressure: PressureData
    budget: BudgetData
    diskCache: DiskCacheData | null
}

interface BucketInfo {
    available: number
    burstCapacity: number
    refillPerSec: number
    pct: number
}

interface CostResponse {
    storage: {
        uptimeMinutes: number
        bucket: { reads: BucketInfo; writes: BucketInfo; lists: BucketInfo; deletes: BucketInfo }
        totals: { reads: number; writes: number; lists: number; deletes: number; egressBytes: number; ingressBytes: number }
        estimatedCost: { reads: string; writes: string; lists: string; deletes: string; egress: string; total: string }
        attribution?: CostAttribution[]
    }
    egress: {
        totalBytes: number
        totalMB: number
        dailyCapGB: number
        usagePct: number
        estimatedCostUSD: number
        resetsAtUtc: string
    }
}

// ── Helpers ──────────────────────────────────────────────────

/** Safe number coercion — treats null/undefined/NaN as 0 */
function safe(v: unknown): number {
    const n = Number(v)
    return Number.isFinite(n) ? n : 0
}

function fmtBytes(bytes: number): string {
    const b = safe(bytes)
    if (b === 0) return '0 B'
    const units = ['B', 'KB', 'MB', 'GB']
    const i = Math.min(Math.floor(Math.log(b) / Math.log(1024)), units.length - 1)
    const val = b / Math.pow(1024, i)
    return `${val < 10 ? val.toFixed(2) : val < 100 ? val.toFixed(1) : Math.round(val)} ${units[i]}`
}

function fmtNum(n: number): string {
    return safe(n).toLocaleString('sv-SE')
}

function fmtPct(rate: number): string {
    return `${(safe(rate) * 100).toFixed(1)}%`
}

type AttributionGroupBy = keyof AttributionKey

const ATTRIBUTION_GROUPS: Array<{ key: AttributionGroupBy; label: string }> = [
    { key: 'workload', label: 'Workload' },
    { key: 'workspaceType', label: 'Workspace type' },
    { key: 'job', label: 'Job' },
    { key: 'origin', label: 'Origin' },
]

function labelForAttributionValue(value: string | null | undefined): string {
    const normalized = value?.trim()
    return normalized && normalized !== 'none' ? normalized : 'none'
}

function costNumber(value: string | undefined): number {
    const parsed = Number((value ?? '').replace(/[^0-9.-]/g, ''))
    return Number.isFinite(parsed) ? parsed : 0
}

// ── Animated Number ──────────────────────────────────────────

function AnimatedNumber({ value, format = 'number' }: {
    value: number
    format?: 'number' | 'bytes' | 'pct' | 'pct-raw' | 'decimal2'
}) {
    const safeValue = safe(value)
    const [display, setDisplay] = useState(safeValue)
    const prevRef = useRef(safeValue)
    const animRef = useRef<number | null>(null)

    useEffect(() => {
        const from = prevRef.current
        const to = safeValue
        prevRef.current = safeValue
        if (from === to) { setDisplay(to); return }

        const duration = 500
        const start = performance.now()
        const step = (now: number) => {
            const t = Math.min((now - start) / duration, 1)
            const ease = 1 - Math.pow(1 - t, 3)
            setDisplay(from + (to - from) * ease)
            if (t < 1) animRef.current = requestAnimationFrame(step)
        }
        if (animRef.current) cancelAnimationFrame(animRef.current)
        animRef.current = requestAnimationFrame(step)
        return () => { if (animRef.current) cancelAnimationFrame(animRef.current) }
    }, [safeValue])

    const d = safe(display)
    let text: string
    switch (format) {
        case 'bytes': text = fmtBytes(Math.round(d)); break
        case 'pct': text = fmtPct(d); break
        case 'pct-raw': text = `${(d * 100).toFixed(1)}%`; break
        case 'decimal2': text = d.toFixed(2); break
        default: text = fmtNum(Math.round(d)); break
    }

    return <>{text}</>
}

// ── Copyable Value ───────────────────────────────────────────

function CopyableValue({ children, raw }: { children: React.ReactNode; raw: string }) {
    const [copied, setCopied] = useState(false)
    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

    const handleClick = useCallback(() => {
        navigator.clipboard.writeText(raw).catch(() => { })
        setCopied(true)
        if (timerRef.current) clearTimeout(timerRef.current)
        timerRef.current = setTimeout(() => setCopied(false), 1200)
    }, [raw])

    return (
        <span
            className={`admin-stat-value admin-copyable ${copied ? 'admin-copied' : ''}`}
            onClick={handleClick}
            title="Click to copy"
        >
            {children}
            <span className="admin-copy-icon">{copied ? '✓' : '⎘'}</span>
        </span>
    )
}

// ── Password Gate ────────────────────────────────────────────

const SESSION_KEY = 'sb:admin-key'

function LoginGate({ onAuth }: { onAuth: (key: string) => void }) {
    const [password, setPassword] = useState('')
    const [showPw, setShowPw] = useState(false)
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState('')
    const [shake, setShake] = useState(false)
    const inputRef = useRef<HTMLInputElement>(null)

    useEffect(() => { inputRef.current?.focus() }, [])

    const handleSubmit = useCallback(async (e: React.FormEvent) => {
        e.preventDefault()
        if (!password.trim()) return
        setLoading(true)
        setError('')

        try {
            const res = await fetch(`${CACHESTATS_URL}`, {
                headers: { 'X-Admin-Key': password.trim() },
            })
            recordUnexpectedAdminResponse(res, CACHESTATS_URL)
            if (res.ok) {
                sessionStorage.setItem(SESSION_KEY, password.trim())
                onAuth(password.trim())
            } else {
                setError(res.status === 401 ? 'Invalid admin key' : `Error: ${res.status}`)
                setShake(true)
                setTimeout(() => setShake(false), 500)
            }
        } catch {
            setError('Could not reach the server')
            setShake(true)
            setTimeout(() => setShake(false), 500)
        } finally {
            setLoading(false)
        }
    }, [password, onAuth])

    return (
        <div className="admin-login-wrapper">
            <div className={`admin-login-card ${shake ? 'admin-shake' : ''}`}>
                <span className="admin-login-icon">🔐</span>
                <h1>Admin Dashboard</h1>
                <p>Enter your admin key to view server statistics</p>
                <form className="admin-login-form" onSubmit={handleSubmit}>
                    <div className="admin-input-wrapper">
                        <input
                            ref={inputRef}
                            className={`admin-input${error ? ' error' : ''}`}
                            type={showPw ? 'text' : 'password'}
                            placeholder="Admin key"
                            value={password}
                            onChange={e => { setPassword(e.target.value); setError('') }}
                            autoComplete="current-password"
                            spellCheck={false}
                        />
                        <button
                            type="button"
                            className="admin-toggle-vis"
                            onClick={() => setShowPw(v => !v)}
                            tabIndex={-1}
                            aria-label={showPw ? 'Hide password' : 'Show password'}
                        >
                            {showPw ? '🙈' : '👁'}
                        </button>
                    </div>
                    <div className="admin-login-error">{error || '\u00A0'}</div>
                    <button
                        type="submit"
                        className="admin-login-btn"
                        disabled={loading || !password.trim()}
                    >
                        {loading ? (
                            <span className="admin-btn-loading">
                                <span className="shared-spinner sm" />
                                Verifying…
                            </span>
                        ) : 'Unlock'}
                    </button>
                </form>
            </div>
        </div>
    )
}

// ── Cache Card ───────────────────────────────────────────────

function CacheCard({ title, icon, accent, data }: {
    title: string; icon: string; accent: string; data: CacheLayer
}) {
    return (
        <div className={`admin-card admin-card--${accent}`}>
            <div className="admin-card-header">
                <span className="admin-card-icon">{icon}</span>
                <span className="admin-card-title">{title}</span>
            </div>
            <div className="admin-hit-rate">
                <span className="admin-hit-rate-label">Hit rate</span>
                <div className="admin-hit-rate-bar">
                    <div
                        className="admin-hit-rate-fill"
                        style={{ width: `${Math.round(safe(data.hitRate) * 100)}%` }}
                    />
                </div>
                <span className="admin-hit-rate-pct">
                    <AnimatedNumber value={data.hitRate} format="pct" />
                </span>
            </div>
            <div className="admin-stat-row">
                <span className="admin-stat-label">Hits</span>
                <CopyableValue raw={String(data.hits)}>
                    <AnimatedNumber value={data.hits} />
                </CopyableValue>
            </div>
            <div className="admin-stat-row">
                <span className="admin-stat-label">Misses</span>
                <CopyableValue raw={String(data.misses)}>
                    <AnimatedNumber value={data.misses} />
                </CopyableValue>
            </div>
            <div className="admin-stat-row">
                <span className="admin-stat-label">Evictions</span>
                <CopyableValue raw={String(data.evictions)}>
                    <AnimatedNumber value={data.evictions} />
                </CopyableValue>
            </div>
            <div className="admin-stat-row">
                <span className="admin-stat-label">Entries</span>
                <CopyableValue raw={String(data.entryCount)}>
                    <AnimatedNumber value={data.entryCount} />
                </CopyableValue>
            </div>
            <div className="admin-stat-row">
                <span className="admin-stat-label">Size</span>
                <CopyableValue raw={String(data.estimatedBytes)}>
                    <AnimatedNumber value={data.estimatedBytes} format="bytes" />
                </CopyableValue>
            </div>
        </div>
    )
}

// ── Pressure Bar ─────────────────────────────────────────────

function PressureBar({ pressure, zone }: { pressure: number; zone: string }) {
    const pct = Math.min(safe(pressure) * 100, 100)
    const zoneLabel = (zone ?? 'green').toLowerCase()
    const barColor = pct < 60 ? 'var(--color-success)' : pct < 80 ? 'var(--color-warning)' : 'var(--color-error)'

    return (
        <div className="admin-pressure-bar-wrap">
            <div className="admin-pressure-bar-header">
                <span className="admin-pressure-bar-label">Memory usage</span>
                <span className={`admin-pressure-bar-pct admin-pressure-bar-pct--${zoneLabel}`}>
                    <AnimatedNumber value={pressure} format="pct" />
                </span>
            </div>
            <div className="admin-pressure-bar">
                <div
                    className="admin-pressure-bar-fill"
                    style={{ width: `${pct}%`, background: barColor }}
                />
                {/* Threshold markers */}
                <div className="admin-pressure-bar-tick" style={{ left: '65%' }} title="Yellow zone" />
                <div className="admin-pressure-bar-tick" style={{ left: '80%' }} title="Red zone" />
            </div>
        </div>
    )
}

// ── Pressure Card ────────────────────────────────────────────

function PressureCard({ data }: { data: PressureData }) {
    const zone = (data.zone ?? 'green').toLowerCase()
    return (
        <div className="admin-card admin-card--wide admin-card--rose">
            <div className="admin-card-header">
                <span className="admin-card-icon">🧠</span>
                <span className="admin-card-title">Memory Pressure</span>
                <span className={`admin-zone-badge admin-zone-badge--${zone}`}>
                    <span className="admin-zone-dot" />
                    {data.zone}
                </span>
            </div>
            <PressureBar pressure={data.pressure} zone={data.zone} />
            <div className="admin-pressure-grid">
                <div className="admin-pressure-item">
                    <span className="admin-pressure-label">Fragmentation</span>
                    <span className="admin-pressure-value">
                        <AnimatedNumber value={data.fragmentation} format="pct" />
                    </span>
                </div>
                <div className="admin-pressure-item">
                    <span className="admin-pressure-label">Gen2 Rate</span>
                    <span className="admin-pressure-value">
                        <AnimatedNumber value={data.gen2Rate} format="decimal2" /> /s
                    </span>
                </div>
                <div className="admin-pressure-item">
                    <span className="admin-pressure-label">Working Set</span>
                    <span className="admin-pressure-value">
                        <AnimatedNumber value={data.workingSetBytes} format="bytes" />
                    </span>
                </div>
                <div className="admin-pressure-item">
                    <span className="admin-pressure-label">Available Memory</span>
                    <span className="admin-pressure-value">
                        <AnimatedNumber value={data.availableMemoryBytes} format="bytes" />
                    </span>
                </div>
                <div className="admin-pressure-item">
                    <span className="admin-pressure-label">Process Pressure</span>
                    <span className="admin-pressure-value">
                        <AnimatedNumber value={data.processPressure} format="pct" />
                    </span>
                </div>
                <div className="admin-pressure-item">
                    <span className="admin-pressure-label">GC Threshold</span>
                    <span className="admin-pressure-value">
                        <AnimatedNumber value={data.highMemoryLoadThresholdBytes} format="bytes" />
                    </span>
                </div>
                <div className="admin-pressure-item">
                    <span className="admin-pressure-label">Last Sample</span>
                    <span className="admin-pressure-value" style={{ fontSize: '0.78rem' }}>
                        {new Date(data.lastSample).toLocaleTimeString('sv-SE')}
                    </span>
                </div>
                <div className="admin-pressure-item">
                    <span className="admin-pressure-label">Compacts (Y / R / C)</span>
                    <span className="admin-pressure-value">
                        {data.compactsYellow} / {data.compactsRed} / {data.compactsCritical}
                    </span>
                </div>
                <div className="admin-pressure-item">
                    <span className="admin-pressure-label">Large Object Evictions</span>
                    <span className="admin-pressure-value">
                        <AnimatedNumber value={data.largeObjectEvictions} />
                    </span>
                </div>
                <div className="admin-pressure-item">
                    <span className="admin-pressure-label">Large Objects Tracked</span>
                    <span className="admin-pressure-value">
                        <AnimatedNumber value={data.largeObjectsTracked} />
                    </span>
                </div>
            </div>
        </div>
    )
}

// ── Budget Card ──────────────────────────────────────────────

function BudgetCard({ data, stats }: { data: BudgetData; stats: StatsResponse }) {
    const items = [
        { label: 'Object Cache', used: stats.objectCache.estimatedBytes, limit: data.objectSizeLimit },
        { label: 'Heads Cache', used: stats.headsCache.estimatedBytes, limit: data.headsSizeLimit },
        { label: 'Config Cache', used: stats.configCache.estimatedBytes, limit: data.configSizeLimit },
    ]

    return (
        <div className="admin-card admin-card--purple">
            <div className="admin-card-header">
                <span className="admin-card-icon">📊</span>
                <span className="admin-card-title">Cache Budgets</span>
            </div>
            {items.map(item => {
                const pct = safe(item.limit) > 0 ? Math.min((safe(item.used) / safe(item.limit)) * 100, 100) : 0
                return (
                    <div key={item.label} className="admin-budget-row">
                        <div className="admin-budget-header">
                            <span className="admin-stat-label">{item.label}</span>
                            <span className="admin-stat-value" style={{ fontSize: '0.78rem' }}>
                                <AnimatedNumber value={item.used} format="bytes" />
                                {' / '}
                                {fmtBytes(item.limit)}
                            </span>
                        </div>
                        <div className="admin-budget-bar">
                            <div
                                className="admin-budget-fill"
                                style={{
                                    width: `${pct}%`,
                                    background: pct > 90 ? 'var(--color-error)' : pct > 70 ? 'var(--color-warning)' : 'var(--card-accent, var(--color-accent))',
                                }}
                            />
                        </div>
                    </div>
                )
            })}
        </div>
    )
}

interface CostAttribution {
    key: AttributionKey
    totals: { reads: number; writes: number; lists: number; deletes: number; egressBytes: number; ingressBytes: number }
    estimatedCost: { reads: string; writes: string; lists: string; deletes: string; egress: string; total: string }
}

// ── L2 Disk Cache Card ──────────────────────────────────────

function DiskCacheCard({
    data,
    onTruncate,
    truncateLoading,
}: {
    data: DiskCacheData
    onTruncate: () => void
    truncateLoading: boolean
}) {
    const pct = safe(data.maxBytes) > 0
        ? Math.min((safe(data.estimatedBytes) / safe(data.maxBytes)) * 100, 100)
        : 0
    const blobHitPct = Math.round(safe(data.blobCache.hitRate) * 100)
    const listHitPct = Math.round(safe(data.listCache.hitRate) * 100)
    const [groupBy, setGroupBy] = useState<AttributionGroupBy>('workload')
    const attributionRows = Array.from((data.attributions ?? []).reduce((map, item) => {
        const group = labelForAttributionValue(item.key?.[groupBy])
        const existing = map.get(group) ?? {
            group,
            blobHits: 0,
            blobMisses: 0,
            avoidedAzureReads: 0,
            avoidedAzureEgressBytes: 0,
        }
        existing.blobHits += safe(item.blobHits)
        existing.blobMisses += safe(item.blobMisses)
        existing.avoidedAzureReads += safe(item.avoidedAzureReads)
        existing.avoidedAzureEgressBytes += safe(item.avoidedAzureEgressBytes)
        map.set(group, existing)
        return map
    }, new Map<string, { group: string; blobHits: number; blobMisses: number; avoidedAzureReads: number; avoidedAzureEgressBytes: number }>()).values())
        .filter(row => row.blobHits > 0 || row.blobMisses > 0 || row.avoidedAzureReads > 0)
        .sort((a, b) => b.blobHits - a.blobHits || b.avoidedAzureEgressBytes - a.avoidedAzureEgressBytes)
        .slice(0, 8)

    return (
        <div className="admin-card admin-card--wide admin-card--indigo">
            <div className="admin-card-header">
                <span className="admin-card-icon">💾</span>
                <span className="admin-card-title">L2 Disk Cache</span>
                <span className={`admin-zone-badge ${data.enabled ? 'admin-zone-badge--green' : 'admin-zone-badge--red'}`}>
                    <span className="admin-zone-dot" />
                    {data.enabled ? 'enabled' : 'disabled'}
                </span>
            </div>

            <div className="admin-budget-row">
                <div className="admin-budget-header">
                    <span className="admin-stat-label">Disk usage</span>
                    <span className="admin-stat-value" style={{ fontSize: '0.78rem' }}>
                        <AnimatedNumber value={data.estimatedBytes} format="bytes" />
                        {' / '}
                        {fmtBytes(data.maxBytes)}
                    </span>
                </div>
                <div className="admin-budget-bar">
                    <div
                        className="admin-budget-fill"
                        style={{
                            width: `${pct}%`,
                            background: pct > 95 ? 'var(--color-error)' : pct > 80 ? 'var(--color-warning)' : 'var(--card-accent, var(--color-accent))',
                        }}
                    />
                </div>
            </div>

            <div className="admin-disk-hit-grid">
                <div>
                    <span className="admin-hit-rate-label">Blob hits</span>
                    <div className="admin-hit-rate-bar">
                        <div className="admin-hit-rate-fill" style={{ width: `${blobHitPct}%` }} />
                    </div>
                    <span className="admin-hit-rate-pct">
                        <AnimatedNumber value={data.blobCache.hitRate} format="pct" />
                    </span>
                </div>
                <div>
                    <span className="admin-hit-rate-label">List hits</span>
                    <div className="admin-hit-rate-bar">
                        <div className="admin-hit-rate-fill" style={{ width: `${listHitPct}%` }} />
                    </div>
                    <span className="admin-hit-rate-pct">
                        <AnimatedNumber value={data.listCache.hitRate} format="pct" />
                    </span>
                </div>
            </div>

            <div className="admin-pressure-grid">
                <div className="admin-pressure-item">
                    <span className="admin-pressure-label">Avoided Azure reads</span>
                    <span className="admin-pressure-value"><AnimatedNumber value={data.avoidedAzureReads} /></span>
                </div>
                <div className="admin-pressure-item">
                    <span className="admin-pressure-label">Avoided Azure lists</span>
                    <span className="admin-pressure-value"><AnimatedNumber value={data.avoidedAzureLists} /></span>
                </div>
                <div className="admin-pressure-item">
                    <span className="admin-pressure-label">Avoided egress</span>
                    <span className="admin-pressure-value"><AnimatedNumber value={data.avoidedAzureEgressBytes} format="bytes" /></span>
                </div>
                <div className="admin-pressure-item">
                    <span className="admin-pressure-label">Entries (blob / list / artifact)</span>
                    <span className="admin-pressure-value">{data.blobEntries} / {data.listEntries} / {data.artifactEntries}</span>
                </div>
                <div className="admin-pressure-item">
                    <span className="admin-pressure-label">List invalidations</span>
                    <span className="admin-pressure-value"><AnimatedNumber value={data.listInvalidations} /></span>
                </div>
                <div className="admin-pressure-item">
                    <span className="admin-pressure-label">Truncated lists not cached</span>
                    <span className="admin-pressure-value"><AnimatedNumber value={data.uncacheableTruncatedLists} /></span>
                </div>
            </div>

            <div className="admin-stat-row">
                <span className="admin-stat-label">Root</span>
                <CopyableValue raw={data.rootPath}>{data.rootPath}</CopyableValue>
            </div>
            <div className="admin-stat-row">
                <span className="admin-stat-label">Maintenance</span>
                <span className="admin-stat-value">
                    trims {fmtNum(data.trimRuns)} · truncates {fmtNum(data.truncateRuns)} · invalid entries purged {fmtNum(data.corruptEntries)}
                    {data.startupScanEnabled && !data.startupScanCompleted ? ' · startup scan pending' : ''}
                </span>
            </div>
            {attributionRows.length > 0 && (
                <div className="admin-attribution-panel">
                    <div className="admin-attribution-header">
                        <span className="admin-attribution-title">L2 blob hits</span>
                        <div className="admin-segmented-control" aria-label="Group L2 blob hits">
                            {ATTRIBUTION_GROUPS.map(group => (
                                <button
                                    key={group.key}
                                    type="button"
                                    className={groupBy === group.key ? 'active' : ''}
                                    onClick={() => setGroupBy(group.key)}
                                >
                                    {group.label}
                                </button>
                            ))}
                        </div>
                    </div>
                    <div className="admin-attribution-table">
                        <div className="admin-attribution-row admin-attribution-row--head">
                            <span>{ATTRIBUTION_GROUPS.find(group => group.key === groupBy)?.label}</span>
                            <span>Hits</span>
                            <span>Hit rate</span>
                            <span>Avoided</span>
                            <span>Egress</span>
                        </div>
                        {attributionRows.map(row => {
                            const total = row.blobHits + row.blobMisses
                            return (
                                <div key={row.group} className="admin-attribution-row">
                                    <span className="admin-attribution-name">{row.group}</span>
                                    <span>{fmtNum(row.blobHits)}</span>
                                    <span>{total > 0 ? fmtPct(row.blobHits / total) : '0.0%'}</span>
                                    <span>{fmtNum(row.avoidedAzureReads)}</span>
                                    <span>{fmtBytes(row.avoidedAzureEgressBytes)}</span>
                                </div>
                            )
                        })}
                    </div>
                </div>
            )}
            {data.lastError && (
                <div className="admin-stat-row">
                    <span className="admin-stat-label">Last error</span>
                    <span className="admin-stat-value admin-stat-value--error">{data.lastError}</span>
                </div>
            )}
            <div className="admin-card-actions">
                <button
                    className="admin-danger-btn"
                    onClick={onTruncate}
                    disabled={truncateLoading}
                    title="Truncate L2 disk cache"
                >
                    {truncateLoading ? 'Truncating…' : 'Truncate L2 cache'}
                </button>
            </div>
        </div>
    )
}

// ── Operations & Cost Card ───────────────────────────────────

function OperationsCard({ cost }: { cost: CostResponse }) {
    const { storage } = cost
    const [groupBy, setGroupBy] = useState<AttributionGroupBy>('workload')
    const buckets = [
        { label: 'Reads', icon: '📖', ...storage.bucket.reads, total: storage.totals.reads },
        { label: 'Writes', icon: '✏️', ...storage.bucket.writes, total: storage.totals.writes },
        { label: 'Lists', icon: '📋', ...storage.bucket.lists, total: storage.totals.lists },
        { label: 'Deletes', icon: '🗑️', ...storage.bucket.deletes, total: storage.totals.deletes },
    ]
    const attributionRows = Array.from((storage.attribution ?? []).reduce((map, item) => {
        const group = labelForAttributionValue(item.key?.[groupBy])
        const existing = map.get(group) ?? {
            group,
            reads: 0,
            writes: 0,
            lists: 0,
            deletes: 0,
            egressBytes: 0,
            totalCost: 0,
        }
        existing.reads += safe(item.totals?.reads)
        existing.writes += safe(item.totals?.writes)
        existing.lists += safe(item.totals?.lists)
        existing.deletes += safe(item.totals?.deletes)
        existing.egressBytes += safe(item.totals?.egressBytes)
        existing.totalCost += costNumber(item.estimatedCost?.total)
        map.set(group, existing)
        return map
    }, new Map<string, { group: string; reads: number; writes: number; lists: number; deletes: number; egressBytes: number; totalCost: number }>()).values())
        .filter(row => row.reads > 0 || row.writes > 0 || row.lists > 0 || row.deletes > 0 || row.egressBytes > 0)
        .sort((a, b) => (b.reads + b.writes + b.lists + b.deletes) - (a.reads + a.writes + a.lists + a.deletes))
        .slice(0, 8)

    return (
        <div className="admin-card admin-card--wide admin-card--teal">
            <div className="admin-card-header">
                <span className="admin-card-icon">⚡</span>
                <span className="admin-card-title">Operations & Cost</span>
            </div>

            <div className="admin-ops-grid">
                {buckets.map(b => {
                    const usagePct = b.burstCapacity > 0
                        ? Math.round((1 - b.available / b.burstCapacity) * 100)
                        : 0
                    return (
                        <div key={b.label} className="admin-ops-item">
                            <div className="admin-ops-header">
                                <span>{b.icon} {b.label}</span>
                                <span className="admin-ops-total">
                                    <AnimatedNumber value={b.total} />
                                </span>
                            </div>
                            <div className="admin-budget-bar">
                                <div
                                    className="admin-budget-fill"
                                    style={{
                                        width: `${usagePct}%`,
                                        background: usagePct > 80 ? 'var(--color-error)' : usagePct > 50 ? 'var(--color-warning)' : 'var(--color-success)',
                                    }}
                                />
                            </div>
                            <div className="admin-ops-meta">
                                <span>{fmtNum(b.available)} / {fmtNum(b.burstCapacity)} tokens</span>
                                <span>{safe(b.refillPerSec).toFixed(1)}/s</span>
                            </div>
                        </div>
                    )
                })}
            </div>

            <div className="admin-cost-summary">
                <span className="admin-cost-label">Estimated cost</span>
                <span className="admin-cost-total">{storage.estimatedCost.total}</span>
                <div className="admin-cost-breakdown">
                    <span>R: {storage.estimatedCost.reads}</span>
                    <span>W: {storage.estimatedCost.writes}</span>
                    <span>L: {storage.estimatedCost.lists}</span>
                    <span>D: {storage.estimatedCost.deletes}</span>
                    <span>E: {storage.estimatedCost.egress}</span>
                </div>
            </div>
            {attributionRows.length > 0 && (
                <div className="admin-attribution-panel">
                    <div className="admin-attribution-header">
                        <span className="admin-attribution-title">Operations by</span>
                        <div className="admin-segmented-control" aria-label="Group operations">
                            {ATTRIBUTION_GROUPS.map(group => (
                                <button
                                    key={group.key}
                                    type="button"
                                    className={groupBy === group.key ? 'active' : ''}
                                    onClick={() => setGroupBy(group.key)}
                                >
                                    {group.label}
                                </button>
                            ))}
                        </div>
                    </div>
                    <div className="admin-attribution-table">
                        <div className="admin-attribution-row admin-attribution-row--head admin-attribution-row--ops">
                            <span>{ATTRIBUTION_GROUPS.find(group => group.key === groupBy)?.label}</span>
                            <span>R</span>
                            <span>W</span>
                            <span>L</span>
                            <span>D</span>
                            <span>Cost</span>
                        </div>
                        {attributionRows.map(row => (
                            <div key={row.group} className="admin-attribution-row admin-attribution-row--ops">
                                <span className="admin-attribution-name">{row.group}</span>
                                <span>{fmtNum(row.reads)}</span>
                                <span>{fmtNum(row.writes)}</span>
                                <span>{fmtNum(row.lists)}</span>
                                <span>{fmtNum(row.deletes)}</span>
                                <span>${row.totalCost.toFixed(4)}</span>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    )
}

// ── Egress Card ──────────────────────────────────────────────

function EgressCard({ egress, storage }: { egress: CostResponse['egress']; storage: CostResponse['storage'] }) {
    const usagePct = Math.min(egress.usagePct, 100)
    const resetDate = new Date(egress.resetsAtUtc)

    return (
        <div className="admin-card admin-card--cyan">
            <div className="admin-card-header">
                <span className="admin-card-icon">🌐</span>
                <span className="admin-card-title">Bandwidth</span>
            </div>
            <div className="admin-budget-row">
                <div className="admin-budget-header">
                    <span className="admin-stat-label">Egress today</span>
                    <span className="admin-stat-value" style={{ fontSize: '0.78rem' }}>
                        {safe(egress.totalMB).toFixed(1)} MB / {safe(egress.dailyCapGB)} GB
                    </span>
                </div>
                <div className="admin-budget-bar">
                    <div
                        className="admin-budget-fill"
                        style={{
                            width: `${usagePct}%`,
                            background: usagePct > 90 ? 'var(--color-error)' : usagePct > 70 ? 'var(--color-warning)' : 'var(--color-accent)',
                        }}
                    />
                </div>
            </div>
            <div className="admin-stat-row">
                <span className="admin-stat-label">Ingress (storage ops)</span>
                <CopyableValue raw={String(storage.totals.ingressBytes)}>
                    <AnimatedNumber value={storage.totals.ingressBytes} format="bytes" />
                </CopyableValue>
            </div>
            <div className="admin-stat-row">
                <span className="admin-stat-label">Egress (storage ops)</span>
                <CopyableValue raw={String(storage.totals.egressBytes)}>
                    <AnimatedNumber value={storage.totals.egressBytes} format="bytes" />
                </CopyableValue>
            </div>
            <div className="admin-stat-row">
                <span className="admin-stat-label">Egress cost</span>
                <span className="admin-stat-value">${safe(egress.estimatedCostUSD).toFixed(4)}</span>
            </div>
            <div className="admin-stat-row">
                <span className="admin-stat-label">Resets at</span>
                <span className="admin-stat-value" style={{ fontSize: '0.78rem' }}>
                    {resetDate.toLocaleTimeString('sv-SE')} UTC
                </span>
            </div>
        </div>
    )
}

// ── Skeleton ─────────────────────────────────────────────────

function SkeletonCard() {
    return (
        <div className="admin-card">
            <div className="admin-skeleton admin-skeleton-line" style={{ '--skel-w': '40%' } as React.CSSProperties} />
            <div className="admin-skeleton admin-skeleton-line" style={{ '--skel-w': '100%' } as React.CSSProperties} />
            <div className="admin-skeleton admin-skeleton-line" style={{ '--skel-w': '75%' } as React.CSSProperties} />
            <div className="admin-skeleton admin-skeleton-line" style={{ '--skel-w': '60%' } as React.CSSProperties} />
            <div className="admin-skeleton admin-skeleton-line" style={{ '--skel-w': '85%' } as React.CSSProperties} />
            <div className="admin-skeleton admin-skeleton-line" style={{ '--skel-w': '55%' } as React.CSSProperties} />
        </div>
    )
}

// ── Auto-refresh progress ────────────────────────────────────

function RefreshProgress({ interval, paused }: { interval: number; paused: boolean }) {
    const [progress, setProgress] = useState(0)
    const startRef = useRef(Date.now())

    useEffect(() => {
        startRef.current = Date.now()
        setProgress(0)
    }, [paused])

    useEffect(() => {
        if (paused) return
        const tick = () => {
            const elapsed = Date.now() - startRef.current
            setProgress(Math.min(elapsed / interval, 1))
        }
        const id = setInterval(tick, 50)
        return () => clearInterval(id)
    }, [interval, paused])

    return (
        <div className="admin-refresh-progress">
            <div
                className="admin-refresh-progress-fill"
                style={{ width: `${progress * 100}%` }}
            />
        </div>
    )
}

// ── Main Dashboard ───────────────────────────────────────────

const AUTO_REFRESH_MS = 15_000

/** Format minutes into human-readable uptime string */
function fmtUptime(totalMinutes: number): string {
    const m = Math.round(safe(totalMinutes))
    if (m < 60) return `${m}m`
    const h = Math.floor(m / 60)
    const rm = m % 60
    if (h < 24) return `${h}h ${rm}m`
    const d = Math.floor(h / 24)
    const rh = h % 24
    return `${d}d ${rh}h`
}

function Dashboard({ adminKey }: { adminKey: string }) {
    const [stats, setStats] = useState<StatsResponse | null>(null)
    const [cost, setCost] = useState<CostResponse | null>(null)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState('')
    const [lastFetch, setLastFetch] = useState<Date | null>(null)
    const [refreshKey, setRefreshKey] = useState(0)
    const [truncateLoading, setTruncateLoading] = useState(false)
    const [notice, setNotice] = useState('')
    const timerRef = useRef<ReturnType<typeof setInterval> | null>(null)

    // Live uptime counter: starts from server value, ticks locally every 60s
    const [uptimeMinutes, setUptimeMinutes] = useState<number | null>(null)
    const uptimeBaseRef = useRef<{ serverMinutes: number; fetchedAt: number } | null>(null)

    useEffect(() => {
        if (uptimeBaseRef.current === null) return
        const tick = () => {
            const elapsed = (Date.now() - uptimeBaseRef.current!.fetchedAt) / 60_000
            setUptimeMinutes(uptimeBaseRef.current!.serverMinutes + elapsed)
        }
        tick()
        const id = setInterval(tick, 60_000)
        return () => clearInterval(id)
    }, [uptimeBaseRef.current?.fetchedAt])

    const fetchStats = useCallback(async (silent = false) => {
        if (!silent) setLoading(true)
        setError('')
        if (!silent) setNotice('')
        try {
            const [statsRes, costRes] = await Promise.all([
                fetch(`${CACHESTATS_URL}`, { headers: { 'X-Admin-Key': adminKey } }),
                fetch(`${COST_URL}`, { headers: { 'X-Admin-Key': adminKey } }),
            ])
            recordUnexpectedAdminResponse(statsRes, CACHESTATS_URL)
            recordUnexpectedAdminResponse(costRes, COST_URL)
            if (!statsRes.ok) {
                if (statsRes.status === 401) {
                    sessionStorage.removeItem(SESSION_KEY)
                    window.location.reload()
                    return
                }
                throw new Error(`cachestats: HTTP ${statsRes.status}`)
            }
            const statsData = await statsRes.json()
            setStats(statsData)

            if (!costRes.ok) {
                if (costRes.status === 401) {
                    sessionStorage.removeItem(SESSION_KEY)
                    window.location.reload()
                    return
                }
                throw new Error(`cost: HTTP ${costRes.status}`)
            }

            const costData = await costRes.json()
            setCost(costData)
            // Sync uptime base from server
            const serverUp = safe(costData.storage?.uptimeMinutes)
            uptimeBaseRef.current = { serverMinutes: serverUp, fetchedAt: Date.now() }
            setUptimeMinutes(serverUp)

            setLastFetch(new Date())
            setRefreshKey(k => k + 1)
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Fetch failed')
        } finally {
            setLoading(false)
        }
    }, [adminKey])

    useEffect(() => {
        fetchStats()
        timerRef.current = setInterval(() => fetchStats(true), AUTO_REFRESH_MS)
        return () => { if (timerRef.current) clearInterval(timerRef.current) }
    }, [fetchStats])

    useEffect(() => {
        const handler = (e: KeyboardEvent) => {
            if ((e.target as HTMLElement)?.tagName === 'INPUT') return
            if (e.key === 'r' || e.key === 'R') { fetchStats(); return }
            if (e.key === 'l' || e.key === 'L') {
                sessionStorage.removeItem(SESSION_KEY)
                window.location.reload()
            }
        }
        window.addEventListener('keydown', handler)
        return () => window.removeEventListener('keydown', handler)
    }, [fetchStats])

    const handleLogout = useCallback(() => {
        sessionStorage.removeItem(SESSION_KEY)
        window.location.reload()
    }, [])

    const handleTruncateDiskCache = useCallback(async () => {
        setTruncateLoading(true)
        setError('')
        setNotice('')
        try {
            const res = await fetch(DISK_CACHE_TRUNCATE_URL, {
                method: 'POST',
                headers: { 'X-Admin-Key': adminKey },
            })
            recordUnexpectedAdminResponse(res, DISK_CACHE_TRUNCATE_URL, 'POST')
            if (!res.ok) {
                if (res.status === 401) {
                    sessionStorage.removeItem(SESSION_KEY)
                    window.location.reload()
                    return
                }
                throw new Error(`disk-cache truncate: HTTP ${res.status}`)
            }

            const payload = await res.json()
            const deletedBytes = safe(payload.deletedBytes)
            const deletedEntries = safe(payload.deletedEntries)
            if (payload.diskCache) {
                setStats(current => current ? { ...current, diskCache: payload.diskCache } : current)
            }
            setNotice(`L2 cache truncated: ${fmtNum(deletedEntries)} entries, ${fmtBytes(deletedBytes)} freed`)
            await fetchStats(true)
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Truncate failed')
        } finally {
            setTruncateLoading(false)
        }
    }, [adminKey, fetchStats])

    const isInitialLoad = loading && !stats

    return (
        <div className="admin-dashboard">
            <RefreshProgress key={refreshKey} interval={AUTO_REFRESH_MS} paused={loading} />

            <div className="admin-header">
                <div className="admin-header-left">
                    <h1>📈 Server Stats</h1>
                    {uptimeMinutes != null && (
                        <span className="admin-header-uptime" title={`${Math.round(uptimeMinutes)} minutes`}>
                            <span className="admin-header-uptime-dot" />
                            {fmtUptime(uptimeMinutes)}
                        </span>
                    )}
                    {lastFetch && (
                        <span className="admin-timestamp">
                            {lastFetch.toLocaleTimeString('sv-SE')}
                        </span>
                    )}
                    <span className="admin-auto-label">auto {AUTO_REFRESH_MS / 1000}s</span>
                </div>
                <div className="admin-header-right">
                    <a href={APP_BASE_PATH} className="admin-back-link">← Back to app</a>
                    <button
                        className="admin-refresh-btn"
                        onClick={() => fetchStats()}
                        disabled={loading}
                        title="Refresh (R)"
                    >
                        <span className={loading ? 'spin' : ''}>↻</span>
                        Refresh
                    </button>
                    <button className="admin-logout-btn" onClick={handleLogout} title="Lock (L)">
                        🔒 Lock
                    </button>
                </div>
            </div>

            {error && (
                <div className="admin-error-banner">⚠️ {error}</div>
            )}
            {notice && (
                <div className="admin-notice-banner">{notice}</div>
            )}

            {isInitialLoad ? (
                <div className="admin-grid">
                    <SkeletonCard />
                    <SkeletonCard />
                    <SkeletonCard />
                    <SkeletonCard />
                    <SkeletonCard />
                    <SkeletonCard />
                </div>
            ) : stats && (
                <>
                    {/* ── Section: System Health ─────────────────────── */}
                    <section className="admin-section">
                        <div className="admin-section-header">
                            <span className="admin-section-label">System Health</span>
                        </div>
                        <div className="admin-grid">
                            <PressureCard data={stats.pressure} />
                        </div>
                    </section>

                    {/* ── Section: Cache Performance ─────────────────── */}
                    <section className="admin-section">
                        <div className="admin-section-header">
                            <span className="admin-section-label">Cache Performance</span>
                        </div>
                        <div className="admin-grid">
                            <CacheCard title="Object Cache" icon="📦" accent="blue" data={stats.objectCache} />
                            <CacheCard title="Heads Cache" icon="🏷️" accent="green" data={stats.headsCache} />
                            <CacheCard title="Config Cache" icon="⚙️" accent="amber" data={stats.configCache} />
                            <BudgetCard data={stats.budget} stats={stats} />
                            {stats.diskCache && (
                                <DiskCacheCard
                                    data={stats.diskCache}
                                    onTruncate={handleTruncateDiskCache}
                                    truncateLoading={truncateLoading}
                                />
                            )}
                        </div>
                    </section>

                    {/* ── Section: Cost & Bandwidth ──────────────────── */}
                    {cost && (
                        <section className="admin-section">
                            <div className="admin-section-header">
                                <span className="admin-section-label">Cost & Bandwidth</span>
                            </div>
                            <div className="admin-grid">
                                <OperationsCard cost={cost} />
                                <EgressCard egress={cost.egress} storage={cost.storage} />
                            </div>
                        </section>
                    )}
                </>
            )}

            <div className="admin-footer">
                <span className="admin-footer-hint">
                    <kbd>R</kbd> refresh · <kbd>L</kbd> lock
                </span>
            </div>
        </div>
    )
}

// ── Page ─────────────────────────────────────────────────────

export default function AdminPage() {
    const [adminKey, setAdminKey] = useState<string | null>(
        () => sessionStorage.getItem(SESSION_KEY)
    )

    return (
        <div className="admin-page">
            {adminKey
                ? <Dashboard adminKey={adminKey} />
                : <LoginGate onAuth={setAdminKey} />
            }
        </div>
    )
}
